import { Box, Progress, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

// Define styles using CSS classes instead of createStyles
const styles = {
  dashboardContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '24px',
    width: '100%',
    maxWidth: '600px',
    margin: '20px auto',
  },

  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '16px',
  },

  stepsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative' as const,
    marginBottom: '32px',
    paddingTop: '12px',
  },

  stepsLine: {
    position: 'absolute' as const,
    top: '24px',
    left: '0',
    right: '0',
    height: '2px',
    backgroundColor: '#e0e0e0',
    zIndex: 1,
  },

  step: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    position: 'relative' as const,
    zIndex: 2,
    width: '20%',
  },

  stepCircle: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '8px',
    transition: 'background-color 0.3s ease',
  },

  stepCircleCompleted: {
    backgroundColor: '#4caf50',
  },

  stepCircleCurrent: {
    backgroundColor: '#1976d2',
  },

  stepLabel: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center' as const,
    transition: 'color 0.3s ease, font-weight 0.3s ease',
    textDecoration: 'none',
  },

  stepLabelCompleted: {
    color: '#4caf50',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  stepLabelCurrent: {
    fontWeight: 600,
    color: '#1976d2',
  },

  progressSection: {
    marginTop: '24px',
  },

  progressLabel: {
    fontSize: '14px',
    color: '#1a1a1a',
    marginBottom: '8px',
  },

  progress: {
    height: '8px',
  },
};

interface SessionStatus {
  status: 'uploading' | 'processing' | 'needs_annotation' | 'rendering' | 'complete';
  progress: number;
}

export function CaptureSessionPage(): JSX.Element {
  const { sessionId } = useParams();
  const [status, setStatus] = useState<SessionStatus>({
    status: 'uploading',
    progress: 0,
  });

  const stages = [
    { id: 'uploading', label: 'Uploading', number: 1 },
    { id: 'processing', label: 'Processing', number: 2 },
    { id: 'needs_annotation', label: 'Needs Annotation', number: 3 },
    { id: 'rendering', label: 'Rendering in 3D', number: 4 },
    { id: 'complete', label: 'Report', number: 5 },
  ];

  useEffect(() => {
    // Start with 'uploading' highlighted
    setStatus({ status: 'uploading', progress: 0 });

    const eventSource = new EventSource(
      `https://sse.copilothealth.com/capture/${sessionId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        if (payload) {
          const newStatus = payload.new_status as SessionStatus['status'];
          const index = stages.findIndex(stage => stage.id === newStatus);
          const progress = Math.round((index / (stages.length - 1)) * 100);

          setStatus({
            status: newStatus,
            progress,
          });

          if (newStatus === 'complete') {
            eventSource.close();
          }
        }
      } catch (error) {
        console.error('Failed to process SSE event:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [sessionId]);

  return (
    <Box style={styles.dashboardContainer}>
      <Title style={styles.title}>Session Dashboard</Title>

      <div style={styles.stepsContainer}>
        <div style={styles.stepsLine} />
        {stages.map((stage) => {
          const isCurrent = status.status === stage.id;
          const isComplete = stages.findIndex(s => s.id === status.status) >
                            stages.findIndex(s => s.id === stage.id);

          return (
            <div key={stage.id} style={styles.step}>
              <div 
                style={{
                  ...styles.stepCircle,
                  ...(isComplete && styles.stepCircleCompleted),
                  ...(isCurrent && styles.stepCircleCurrent),
                }}
              >
                {stage.number}
              </div>
              {isComplete ? (
                <Link 
                  to={`/Patient/${sessionId}/capture/${sessionId}/${stage.id}`}
                  style={{
                    ...styles.stepLabel,
                    ...styles.stepLabelCompleted,
                  }}
                >
                  {stage.label}
                </Link>
              ) : (
                <Text 
                  style={{
                    ...styles.stepLabel,
                    ...(isCurrent && styles.stepLabelCurrent),
                  }}
                >
                  {stage.label}
                </Text>
              )}
            </div>
          );
        })}
      </div>

      <div style={styles.progressSection}>
        <Text style={styles.progressLabel}>
          Progress: {stages.find(stage => stage.id === status.status)?.label}
        </Text>
        <Progress
          value={status.progress}
          style={styles.progress}
          radius="sm"
          color="#1976d2"
        />
      </div>
    </Box>
  );
}